// Active-round persistence for CaddieGames.
//
// A round becomes "active" the moment a course is selected and stays
// active until End Round is invoked from the Leaderboard modal. We
// snapshot the full course (rather than storing a courseId) so history
// rounds remain renderable even if mockCourses is later edited.
//
// Storage is localStorage, debounced one level up in CaddieGames so
// rapid score-typing doesn't pummel the disk. A schemaVersion guards
// against future shape changes — on hydrate, version mismatch is
// treated as "no active round" rather than crashing.

import { Course, Player, ScoreMap } from "./types";
import { loadOwner } from "./roster";
import { GAMES_CATALOG, defaultOptionsFor } from "./gamesCatalog";
import { queueBackendPush } from "./caddieGamesStore";

// Per-active-game payload carried in the round. The `id` references a
// game in gamesCatalog; `options` is a snapshot of the option values at
// the moment the user enabled the game (so changing catalog defaults
// later doesn't retroactively rewrite an in-progress round). `enabled`
// is a soft toggle — the user can pause/resume a game without losing
// their option configuration.
export interface ActiveGame {
  id: string;
  enabled: boolean;
  options: Record<string, boolean | number | string>;
}

const ACTIVE_KEY = "asksmart.caddiegames.activeRound";
const HISTORY_KEY = "asksmart.caddiegames.roundsHistory";
const SCHEMA_VERSION = 1;

// Per-Par-3 Oosler/Woosler selection captured live during the round.
// Key = 1-based hole number; value = playerId of the player who hit the
// green first (the "Oosler"), or null when no one was on (carry to the
// next Par 3). Optional on the round so legacy saves hydrate cleanly.
export type OoslerSelections = Record<number, string | null>;

// Per-hole Wolf decision captured live during the round. The Wolf rotates by
// hole (P1, P2, P3, P4, repeat) and, after the tee shots, either picks a
// partner or plays solo. Key = 1-based hole number.
//   • partnerId = a playerId  → 2-v-2 (Wolf + that partner vs the other two)
//   • partnerId = null, blind = false → Lone Wolf (solo, declared after shots)
//   • partnerId = null, blind = true  → Blind Wolf (solo, declared before any
//                                       tee shot — its win/loss is multiplied)
export interface WolfSelection {
  partnerId: string | null;
  blind: boolean;
}
export type WolfSelections = Record<number, WolfSelection>;

// Per-hole Bingo Bango Bongo winners, captured live (Bingo = first on green,
// Bango = closest once all on, Bongo = first in the cup). Every hole awards all
// three to a player. Key = 1-based hole number.
export interface BingoSelection {
  bingo: string;
  bango: string;
  bongo: string;
}
export type BingoSelections = Record<number, BingoSelection>;

export interface ActiveRound {
  id: string;
  course: Course;
  players: Player[];
  scores: ScoreMap;
  displayTeeId?: string;
  // Games configured for this round (any subset of gamesCatalog).
  // Optional for backwards compat with rounds saved before this field
  // existed — persisted rounds without `games` simply default to [].
  games?: ActiveGame[];
  // Oosler/Woosler per-hole selections. Only present when the game is
  // (or was) enabled on the round; absent on rounds that never played
  // it. Carry-forward is computed at scoring time from the run of null
  // entries, so we don't store it explicitly.
  ooslerSelections?: OoslerSelections;
  // Wolf per-hole partner/solo decisions. Only present when Wolf is (or was)
  // enabled. Absent holes simply haven't been decided yet (the runner skips
  // them — no settlement, no carry).
  wolfSelections?: WolfSelections;
  // Bingo Bango Bongo per-hole winners. Only present when BBB is (or was)
  // enabled; absent holes haven't been decided yet.
  bingoSelections?: BingoSelections;
  // Set when this round was started from a tournament. Its presence locks the
  // roster (players / handicaps / tees) in the scorecard — the group is fixed
  // by the admin. The `games` list above stays the player's own side games and
  // is NOT linked to the tournament's games.
  tournamentId?: string;
  // The tournament's display name — surfaced as the title on the History
  // results view (so it reads the tournament name, not just the club).
  tournamentName?: string;
  // Total players across ALL groups in the tournament (not just this group) —
  // shown on the History row so a tournament round reads its full field size.
  tournamentPlayerCount?: number;
  // The player's group within the tournament — used to submit this group's
  // scores so they can be merged into the cross-group leaderboard.
  tournamentGroupId?: string;
  tournamentGroupName?: string;
  // The tournament's games, kept separate from the player's side games so the
  // "Tournament" leaderboard tab can score them without touching the player's
  // own Games list. Only present on tournament rounds.
  tournamentGames?: ActiveGame[];
  startedAt: string;
  lastTouchedAt: string;
  schemaVersion: number;
}

export interface RoundHistoryEntry extends ActiveRound {
  endedAt: string;
  // Present only on a recipient's copy of a round another player shared with
  // them (the value is the owner's userId). Absent on rounds I recorded
  // myself. Drives the "owner can delete for everyone" choice and the
  // reconciliation that drops a shared round once its server copy disappears.
  sharedOwnerId?: string;
}

function newRoundID(): string {
  return `r_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createActiveRound(course: Course): ActiveRound {
  const now = new Date().toISOString();

  // Seed Player 1 as the owner if a profile exists. Skipped silently
  // when the owner hasn't been set up — a brand-new user just gets
  // empty slots like before. Tee defaults to the middle of the course
  // tee list to match the Players modal's auto-tee logic.
  const players: Player[] = [];
  const owner = loadOwner();
  if (owner) {
    const tees = course.tees || [];
    const defaultTeeId =
      tees.length > 0
        ? tees[Math.floor(tees.length / 2)].id
        : undefined;
    players.push({
      id: `p_${Math.random().toString(36).slice(2, 10)}`,
      name: owner.name,
      handicap: owner.handicapIndex,
      tee: defaultTeeId,
    });
  }

  // Seed mandatory games (e.g. Stroke Play). The Games modal also
  // back-fills these on open to cover historical rounds, but seeding
  // here means scoring-side consumers see them from round zero.
  const games: ActiveGame[] = GAMES_CATALOG.filter((g) => g.mandatory).map(
    (g) => ({
      id: g.id,
      enabled: true,
      options: defaultOptionsFor(g.id),
    }),
  );

  return {
    id: newRoundID(),
    course,
    players,
    scores: {},
    games,
    startedAt: now,
    lastTouchedAt: now,
    schemaVersion: SCHEMA_VERSION,
  };
}

// Build a round pre-seeded with a known course, players, and games — used by
// the tournament invite flow, where the admin has already chosen all three so
// the player drops straight into scoring instead of setting up. Unlike
// createActiveRound it does NOT seed the owner or mandatory games; the caller
// supplies the exact roster + game set the tournament defined.
export function createSeededRound(
  course: Course,
  players: Player[],
  games: ActiveGame[],
  tournamentId?: string,
): ActiveRound {
  const now = new Date().toISOString();
  return {
    id: newRoundID(),
    course,
    players,
    scores: {},
    games,
    tournamentId,
    startedAt: now,
    lastTouchedAt: now,
    schemaVersion: SCHEMA_VERSION,
  };
}

export function loadActiveRound(): ActiveRound | null {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.schemaVersion !== SCHEMA_VERSION) return null;
    return parsed as ActiveRound;
  } catch {
    return null;
  }
}

export function saveActiveRound(round: ActiveRound): void {
  try {
    localStorage.setItem(ACTIVE_KEY, JSON.stringify(round));
  } catch {
    // private mode / quota — best effort
  }
  queueBackendPush();
}

export function clearActiveRound(): void {
  try {
    localStorage.removeItem(ACTIVE_KEY);
  } catch {
    // ignore
  }
  queueBackendPush();
}

export function loadHistory(): RoundHistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function appendHistory(entry: RoundHistoryEntry): void {
  const list = loadHistory();
  list.unshift(entry); // newest first
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
  queueBackendPush();
}

export function deleteHistoryEntry(id: string): void {
  const list = loadHistory().filter((r) => r.id !== id);
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
  queueBackendPush();
}
